import { Injectable } from '@angular/core';
import {
	PluginType,
	PluginDetectiveGameData,
	DetectiveDeductionClue,
	DetectiveCaseFile,
	ClueKeyword,
} from '@meta-press/shared-plugins';
import {
	IGameGenerationService,
	GenerationRequest,
	GenerationResult,
	GenerationPrompts,
	ValidationResult,
} from './base/game-generation.interface';
import { SettingsService } from '../settings-service/settings.service';

/**
 * Detective deduction game specific generation service
 */
@Injectable({
	providedIn: 'root',
})
export class DetectiveGenerationService
	implements IGameGenerationService<PluginDetectiveGameData>
{
	readonly pluginType = PluginType.Detective;

	/**
	 * Create system and user prompts for detective deduction game generation
	 * @param request The generation request
	 * @param settingsService Settings service for language preferences
	 * @returns System and user prompts
	 */
	createPrompts(
		request: GenerationRequest,
		settingsService: SettingsService,
	): GenerationPrompts {
		const { parameters, sourceMaterial } = request;

		let systemPrompt: string;
		let userPrompt: string;

		// Get target language prompt from settings service
		const targetLanguagePrompt = settingsService.GetTargetLanguagePrompt();

		const detectiveTopic = parameters['detectiveTopic'] || 'Mysteeritapaus';
		const caseFileCount = parameters['caseFileCount'] || 4;
		const questionCount = parameters['questionCount'] || 3;
		const extraClueCount = parameters['extraClueCount'] || 2;
		const gameDuration = parameters['gameDuration'] || 1; // 0: Lyhyt, 1: Keskipitkä, 2: Pitkä

		// Determine word count specification based on gameDuration
		const wordCountSpec = gameDuration === 0 ? '200-400 sanaa per asiakirja' :
		                      gameDuration === 1 ? '300-600 sanaa per asiakirja' :
		                      '500-800 sanaa per asiakirja';

		// Calculate keywords needed for AI generation
		// Each keyword becomes one game clue that players can collect
		const correctKeywordCount = questionCount * 2; // Each question needs 2 keywords as answer
		const totalKeywordCount = correctKeywordCount + extraClueCount; // Add extra red herring keywords

		// Calculate keyword distribution across case files
		const keywordsPerFile = Math.floor(totalKeywordCount / caseFileCount);
		const remainingKeywords = totalKeywordCount % caseFileCount;

		if (sourceMaterial && sourceMaterial.fileHandles.length > 0) {
			// Source material based generation
			systemPrompt = `Olet asiantuntijakirjoittaja, joka on erikoistunut interaktiivisiin etsiväpeleihin. Tehtäväsi on luoda etsiväpäättelypeli opetuspeliä varten lähdemateriaalin perusteella. Muotoile vastauksesi validina JSON-muotona.`;

			userPrompt = `Luo minulle etsiväpäättelypeli lähdemateriaalin perusteella. Tutki lähdemateriaalista kiinnostavia tapahtumia ja mysteereitä file_search-työkalulla.

Peli tarvitsee:
- ${caseFileCount} tekstiä (kuten tutkimusasiakirja)
- ${questionCount} päättelykysymystä
- ${correctKeywordCount} oikeaa johtolankasanaa (kaksi johtolankasanaa jokaista päättelykysymystä kohden)
- ${extraClueCount} ylimääräistä johtolankasanaa (harhautuksia)
- Yhteensä ${totalKeywordCount} johtolankasanaa

TEKSTIEN PITUUS JA MUOTO:
- Tekstien täytyy toimia itsenäisinä kokonaisuuksinaan ilman, että pelaaja on lukenut alkuperäisen lähdemateriaalin
- Kirjoita asiakirjat TÄYSINÄ KAPPALEINA virkkeineen, ÄLÄ listapisteinä tai ranskalaisina viivoina
- Tavoitepituus: ${wordCountSpec}
- Jaa tekstisisältö informatiivisiin kappaleisiin, jotka sisältävät yksityiskohtia ja kontekstia

JOHTOLANKASANOJEN JAKAUMA:
- Jaa ${totalKeywordCount} johtolankasanaa mahdollisimman tasaisesti ${caseFileCount} tekstin kesken
- ${keywordsPerFile} johtolankasanaa per teksti${remainingKeywords > 0 ? `, ${remainingKeywords} tekstiä sisältää yhden ylimääräisen johtolankasanan` : ''}
- Varmista että jokainen asiakirja sisältää sopivan määrän avainsanoja

PELIMEKANIIKAT:
- Pelaaja kerää johtolankasanoja klikkaamalla niitä teksteissä
- Pelaaja vastaa päättelykysymyksiin valitsemalla 2 oikeaa, päättelykysymykseen loogisesti liittyvää johtolankasanaa
- Jokainen päättelykysymys vaatii täsmälleen 2 oikeaa johtolankasanaa vastaukseksi

PÄÄTTELYKYSYMYSTEN RAKENNE:
Päättelykysymysten täytyy vaatia todellista päättelyä kahden johtolangan yhdistämisestä. Kysymystyypit:

1. KAUSAALISUUS (Syy-seuraus): "Mikä selittää tapahtuman X?"
   - Yksi johtolanka tarjoaa syyn, toinen vahvistaa seurauksen

2. AJALLINEN JÄRJESTYS: "Milloin tapahtuma X tapahtui?"
   - Yksi johtolanka antaa aikaviitteen, toinen vahvistaa tai rajaa aikaikkunaa

3. IDENTITEETTI: "Kuka henkilö/asia X on?"
   - Kaksi johtolankaa viittaavat samaan henkilöön/asiaan eri näkökulmista

4. RISTIRIIDAN RATKAISU: "Kumpi väite on totta?"
   - Kaksi johtolankaa paljastavat ristiriidan tai vahvistavat totuuden

5. MENETELMÄ/KEINO: "Miten X tapahtui?"
   - Kaksi johtolankaa yhdessä paljastavat menetelmän

KRIITTISEN TÄRKEÄÄ PÄÄTTELYKYSYMYKSISSÄ:
- Kysymys ei saa olla vastattavissa vain yhdellä johtolangalla
- Molemmat johtolangat ovat välttämättömiä oikean vastauksen muodostamiseksi
- Kysymyksen täytyy olla muotoiltu niin, että se vaatii päättelyä, ei pelkkää tiedon poimintaa
- Vastaus täytyy olla loogisesti johdettavissa kahden johtolangan yhdistelmästä

KAHDEN JOHTOLANGAN LOOGINEN YHTEYS:
Kaksi oikeaa johtolankaa muodostavat päättelykysymykseen loogisen kokonaisuuden seuraavien mallien mukaan:

A. MOTIIVI + MAHDOLLISUUS: Ensimmäinen johtolanka antaa motiivin (miksi), toinen antaa mahdollisuuden (kuka pystyi)

B. TODISTUS + VAHVISTUS: Ensimmäinen johtolanka antaa suoran todisteen, toinen vahvistaa sen riippumattomasti

C. VÄITE + KUMOUS: Ensimmäinen johtolanka esittää väitteen, toinen vahvistaa tai kumoaa sen objektiivisesti

D. OSA 1 + OSA 2: Johtolangat täydentävät toisiaan muodostaen kokonaiskuvan (esim. ennen + jälkeen)

E. YLEINEN + ERITYINEN: Ensimmäinen antaa yleisen kategorian, toinen spesifin yksityiskohdan

PÄÄTTELYLOGIIKAN TARKISTUS:
Jokaisen päättelykysymyksen looginen ketju:
1. PREMISSI 1: Ensimmäinen johtolanka esittää tosiseikan A
2. PREMISSI 2: Toinen johtolanka esittää tosiseikan B
3. PÄÄTELMÄ: Tosiseikat A ja B yhdessä johtavat loogisesti johtopäätökseen C
4. VASTAUS: Johtopäätös C vastaa päättelykysymykseen

HARHAUTUSTEN SUUNNITTELU:
Ylimääräiset johtolangat (harhautukset):
- Liittyvät tapaukseen ja vaikuttavat relevan­teilta
- Eivät muodosta loogista paria minkään muun johtolangan kanssa
- Voivat olla osittain tosia mutta epätäydellisiä
- Eivät saa olla ristiriidassa oikeiden johtolankojen kanssa

TODISTEIDEN TYYPIT:
Käytä monipuolisia todistetyyppejä: todistajanlausunnot, fyysiset esineet, aikaleimoja, sijaintitietoja, motiiveja, asiakirjatietoja. Yhdistä eri tyyppejä samassa kysymyksessä.

ESIMERKKI:
"Kuka kavalsi varoja?" → Johtolanka 1: "velkoja" (motiivi), Johtolanka 2: "pankkiautomaatti" (mahdollisuus) → Looginen yhteys: MOTIIVI + MAHDOLLISUUS

JOHTOLANKASANOJEN VAATIMUKSET:

KRIITTISEN TÄRKEÄÄ - KAKSI ERI KENTTÄÄ:
Jokainen johtolanka sisältää KAKSI erillistä kenttää, joilla on ERI tarkoitukset:
1. "keyword.word" = TÄSMÄLLINEN KOPIO tekstistä (PAKOLLINEN että löytyy tekstistä)
2. "name" = Johtolangan näyttönimi (voi olla perusmuoto tai muu muoto, käytetään vain näyttämiseen)

VAROITUS: ÄLÄ SEKOITA NÄITÄ KENTTIÄ!
- keyword.word TÄYTYY olla TÄSMÄLLINEN KOPIO tekstistä
- name voi olla eri muoto (esim. perusmuoto)

JOHTOLANKASANOJEN VALINTA JA LUOMINEN:
1. Valitse johtolankasanat asiakirjan pääkäsitteistä ja kokonaisuuden kannalta tärkeistä asioista
2. Johtolankasanojen TÄYTYY löytyä asiakirjojen "content"-kentistä

PAKOLLINEN PROSESSI JOKAISELLE JOHTOLANKASANALLE:

VAIHE 1 - ETSI TEKSTISTÄ:
- Etsi tekstistä sopiva sana tai sanapari joka on tärkeä tapauksen kannalta
- Käytä tekstihaku-toimintoa löytääksesi sanan sijainnin tekstissä
- Varmista että sana todella esiintyy tekstissä

VAIHE 2 - KOPIOI TÄSMÄLLEEN:
- KOPIOI sana SUORAAN tekstistä (älä kirjoita muistista)
- SÄILYTÄ täsmälleen sama:
  * Taivutusmuoto (esim. "korvakorussa", EI "korvakoru")
  * Kirjoitusasu (isot/pienet kirjaimet eivät ole kriittisiä, mutta muoto on)
  * Sanajärjestys (jos monisanainen: "maalattu puulle", EI "puulle maalattu")
  * Välilyönnit (jos monisanainen)

VAIHE 3 - ASETA OIKEIN JSON-RAKENTEESEEN:
- KOPIOIMASI täsmällinen muoto → keyword.word kenttään
- Näyttönimi (voi olla perusmuoto) → name kenttään

ESIMERKKEJÄ:

✅ OIKEIN:
Teksti: "Uhri kantoi korvakorussa pientä timanttia"
→ keyword.word: "korvakorussa" (täsmällinen kopio)
→ name: "korvakoru" (perusmuoto näyttöä varten)

Teksti: "Maalaus oli tehty öljyväreillä kankaalle"
→ keyword.word: "öljyväreillä" (täsmällinen kopio)
→ name: "öljyväri" (perusmuoto näyttöä varten)

Teksti: "Todistaja näki sinisen auton pakenemassa"
→ keyword.word: "sinisen auton" (täsmällinen kopio, monisanainen)
→ name: "sininen auto" (perusmuoto näyttöä varten)

❌ VÄÄRIN:
Teksti: "Uhri kantoi korvakorussa pientä timanttia"
→ keyword.word: "korvakoru" (VÄÄRIN - perusmuoto ei ole tekstissä!)
→ name: "korvakoru"

Teksti: "Todistaja näki sinisen auton pakenemassa"
→ keyword.word: "sininen auto" (VÄÄRIN - perusmuoto ei ole tekstissä!)
→ name: "sininen auto"

Teksti: "Todistaja näki sinisen auton pakenemassa"
→ keyword.word: "auton sinisen" (VÄÄRIN - väärä sanajärjestys!)
→ name: "sininen auto"

VALIDOINTI (PAKOLLINEN):
Ennen johtolangan lisäämistä clues-taulukkoon:
1. Etsi keyword.word tekstistä tekstihakutoiminnolla
2. Jos EI löydy → ÄLÄ lisää johtolankaa, valitse toinen sana
3. Jos löytyy → varmista että se on TÄSMÄLLEEN sama merkkijono (taivutus, järjestys, välilyönnit)
4. Tallenna myös caseFileIndex: miltä tekstiltä (0, 1, 2, jne.) sana löytyy

MUUT VAATIMUKSET:
- Jokainen keyword.word saa esiintyä vain KERRAN koko pelissä
- Johtolankasanat eivät saa olla tyhjiä tai sisältää vain välilyöntejä
- Kahden johtolankasanan täytyy muodostaa looginen yhteys päättelykysymykseen (MOTIIVI + MAHDOLLISUUS, TODISTUS + VAHVISTUS, jne.)

HENKILÖIDEN MAINITSEMINEN TEKSTEISSÄ:
- Kun henkilö mainitaan ENSIMMÄISTÄ KERTAA asiakirjassa, käytä TÄYTTÄ NIMEÄ (etu- ja sukunimi)
- Anna konteksti: kerro kuka henkilö on ja mikä hänen roolinsa on tapauksessa
  * Esimerkki: "Todistaja Matti Virtanen, 45-vuotias naapuri, kertoi nähneensä..."
  * Esimerkki: "Epäilty Liisa Korhonen, uhrin entinen työkaveri, väitti olleensa..."
- Myöhemmissä maininnoissa voit käyttää pelkkää sukunimeä, kun henkilö on jo esitelty
- Jos henkilön nimi on johtolankasana, varmista että tekstissä mainitaan:
  1. Henkilön koko nimi (etu- ja sukunimi)
  2. Henkilön rooli/asema (todistaja, uhri, epäilty, asiantuntija, jne.)
  3. Henkilön yhteys tapaukseen (miksi hän on mukana tässä asiassa)

TEKSTIEN MUOTOILU:
- TÄRKEÄÄ: tekstien otsikot (title) täytyy pitää lyhyinä ja ytimekkäinä, maksimissaan 3 sanaa
- KRIITTISEN TÄRKEÄÄ: Kirjoita asiakirjat TÄYSINÄ KAPPALEINA kokonaisina virkkeinä, ÄLÄ listapisteinä tai ranskalaisina viivoina
- Tavoitepituus: ${wordCountSpec} - käytä laajoja, yksityiskohtaisia kappaleita
- Analysoi tekstin otsikko ja kirjoita sisältö sopivassa muodossa
- "Poliisiraportti", "Tutkintakertomus" → virallisen poliisidokumentin tyyli
- "Todistajanlausunto", "Kuulustelu" → haastattelumerkinnät
- "Rikospaikkatutkinta" → tekninen raportti
- "Lääkärinlausunto" → lääketieteellinen dokumentti
- Käytä asianmukaista terminologiaa ja rakennetta kullekin asiakirjatyypille
- Jaa pidemmät tekstit selkeisiin kappaleisiin käyttämällä rivinvaihtoja (\n\n)
- Käytä asiakirjatyypille sopivia alaotsikoita (esim. "TAPAHTUMAPAIKKA:", "TODISTEET:", "HAVAINNOT:")
- Lisää tyhjä rivi alaotsikoiden jälkeen paremman luettavuuden vuoksi
- Käytä rivinvaihtoja erottamaan eri asiakokonaisuudet selkeästi toisistaan

VASTAUKSEN MUOTO:
Anna YKSI täydellinen JSON-objekti kaikilla kentillä (title, caseFiles, clues, deductionQuestions, successMessage, failureMessage, requiredClueCount). Älä jaa osiin.

JSON-rakenne:
{
  "title": "Pelin otsikko (2-3 sanaa)",
  "mystery": "Lyhyt kuvaus mysteeristä",
  "introText": "Johdantoteksti joka esittelee tapauksen - EI pelimekaniikka-ohjeita",
  "caseFiles": [{"title": "Asiakirjan otsikko", "content": "Asiakirjan sisältö"}],
  "clues": [{"id": "clue_1", "name": "Johtolangan nimi", "description": "Kuvaus", "keyword": {"word": "avainsana", "caseFileIndex": 0}}],
  "deductionQuestions": [{"id": "question_1", "question": "Päättelykysymys", "correctKeywords": ["avainsana1", "avainsana2"]}],
  "successMessage": "Onnistumisviesti",
  "failureMessage": "Epäonnistumisviesti",
  "requiredClueCount": ${correctKeywordCount}
}

GENEROINTIJÄRJESTYS (PAKOLLINEN - NOUDATA TÄTÄ JÄRJESTYSTÄ):
1. Luo ENSIN kaikki ${caseFileCount} tekstiä "caseFiles"-taulukkoon
   - Kirjoita tekstit kokonaan valmiiksi ENNEN kuin siirryt johtolankoihin
   - Varmista että tekstit sisältävät riittävästi tärkeitä sanoja/käsitteitä

2. SITTEN luo ${totalKeywordCount} johtolankaa "clues"-taulukkoon
   - Käy JOKAINEN teksti läpi
   - ETSI ja KOPIOI täsmälliset sanat teksteistä → keyword.word -kenttiin
   - Luo näyttönimet → name -kenttiin
   - VARMISTA että JOKAINEN keyword.word löytyy tekstistä (käytä tekstihakua)
   - Tallenna caseFileIndex: mistä tekstistä sana löytyy

3. SITTEN luo ${questionCount} kysymystä "deductionQuestions"-taulukkoon
   - KRIITTINEN: correctKeywords-arvot TÄYTYY kopioida TÄSMÄLLEEN samat kuin clues[].keyword.word -kentissä
   - ÄLÄ KEKSI uusia keyword-arvoja tässä vaiheessa

4. LOPUKSI validoi ennen JSON:n palauttamista:
   - Jokainen clues[].keyword.word löytyy caseFiles[].content -tekstistä? (Etsi tekstihakutoiminnolla!)
   - Jokainen correctKeywords-arvo löytyy clues[].keyword.word -taulukosta?
   - correctKeywords sisältää tasan 2 eri avainsanaa per kysymys?
   - Jos jokin validointi epäonnistuu → KORJAA virhe, ÄLÄ palauta virheellistä dataa

TÄRKEÄÄ:
- Generoi täsmälleen ${totalKeywordCount} johtolankaa ja ${questionCount} kysymystä
- correctKeywords-arvot TÄYTYY olla TÄSMÄLLEEN SAMAT kuin clues[].keyword.word (sama kirjoitusasu, taivutus, välilyönnit)
- Johtolankasanojen täytyy löytyä caseFiles[].content -teksteistä täsmälleen samassa muodossa

${targetLanguagePrompt}`;
		} else {
			// Topic-based generation
			systemPrompt = `Olet asiantuntijakirjoittaja, joka on erikoistunut interaktiivisiin etsiväpeleihin. Tehtäväsi on luoda etsivädeduktiopeli opetuspeliä varten annetun aiheen perusteella. Muotoile vastauksesi validina JSON-muotona.`;

			userPrompt = `Luo minulle etsivädeduktiopeli aiheesta "${detectiveTopic}".

Peli tarvitsee:
- ${caseFileCount} asiakirjaa (tekstit)
- ${questionCount} päättelykysymystä
- ${correctKeywordCount} oikeaa johtolankasanaa (kaksi johtolankasanaa jokaista päättelykysymystä kohden)
- ${extraClueCount} ylimääräistä johtolankasanaa (harhautuksia)
- Yhteensä ${totalKeywordCount} johtolankasanaa

TEKSTIEN PITUUS JA MUOTO:
- Tekstien täytyy toimia itsenäisinä kokonaisuuksinaan ilman, että pelaaja on lukenut alkuperäisen lähdemateriaalin
- Kirjoita asiakirjat TÄYSINÄ KAPPALEINA virkkeineen, ÄLÄ listapisteinä tai ranskalaisina viivoina
- Tavoitepituus: ${wordCountSpec}
- Jaa tekstisisältö informatiivisiin kappaleisiin, jotka sisältävät yksityiskohtia ja kontekstia

JOHTOLANKASANOJEN JAKAUMA:
- Jaa ${totalKeywordCount} johtolankasanaa mahdollisimman tasaisesti ${caseFileCount} tekstin kesken
- ${keywordsPerFile} johtolankasanaa per teksti${remainingKeywords > 0 ? `, ${remainingKeywords} tekstiä sisältää yhden ylimääräisen johtolankasanan` : ''}
- Varmista että jokainen asiakirja sisältää sopivan määrän avainsanoja

PELIMEKANIIKAT:
- Pelaaja kerää johtolankasanoja klikkaamalla niitä teksteissä
- Pelaaja vastaa päättelykysymyksiin valitsemalla 2 oikeaa, päättelykysymykseen loogisesti liittyvää johtolankasanaa
- Jokainen päättelykysymys vaatii täsmälleen 2 oikeaa johtolankasanaa vastaukseksi

PÄÄTTELYKYSYMYSTEN RAKENNE:
Päättelykysymysten täytyy vaatia todellista päättelyä kahden johtolangan yhdistämisestä. Kysymystyypit:

1. KAUSAALISUUS (Syy-seuraus): "Mikä selittää tapahtuman X?"
   - Yksi johtolanka tarjoaa syyn, toinen vahvistaa seurauksen

2. AJALLINEN JÄRJESTYS: "Milloin tapahtuma X tapahtui?"
   - Yksi johtolanka antaa aikaviitteen, toinen vahvistaa tai rajaa aikaikkunaa

3. IDENTITEETTI: "Kuka henkilö/asia X on?"
   - Kaksi johtolankaa viittaavat samaan henkilöön/asiaan eri näkökulmista

4. RISTIRIIDAN RATKAISU: "Kumpi väite on totta?"
   - Kaksi johtolankaa paljastavat ristiriidan tai vahvistavat totuuden

5. MENETELMÄ/KEINO: "Miten X tapahtui?"
   - Kaksi johtolankaa yhdessä paljastavat menetelmän

KRIITTISEN TÄRKEÄÄ PÄÄTTELYKYSYMYKSISSÄ:
- Kysymys ei saa olla vastattavissa vain yhdellä johtolangalla
- Molemmat johtolangat ovat välttämättömiä oikean vastauksen muodostamiseksi
- Kysymyksen täytyy olla muotoiltu niin, että se vaatii päättelyä, ei pelkkää tiedon poimintaa
- Vastaus täytyy olla loogisesti johdettavissa kahden johtolangan yhdistelmästä

KAHDEN JOHTOLANGAN LOOGINEN YHTEYS:
Kaksi oikeaa johtolankaa muodostavat päättelykysymykseen loogisen kokonaisuuden seuraavien mallien mukaan:

A. MOTIIVI + MAHDOLLISUUS: Ensimmäinen johtolanka antaa motiivin (miksi), toinen antaa mahdollisuuden (kuka pystyi)

B. TODISTUS + VAHVISTUS: Ensimmäinen johtolanka antaa suoran todisteen, toinen vahvistaa sen riippumattomasti

C. VÄITE + KUMOUS: Ensimmäinen johtolanka esittää väitteen, toinen vahvistaa tai kumoaa sen objektiivisesti

D. OSA 1 + OSA 2: Johtolangat täydentävät toisiaan muodostaen kokonaiskuvan (esim. ennen + jälkeen)

E. YLEINEN + ERITYINEN: Ensimmäinen antaa yleisen kategorian, toinen spesifin yksityiskohdan

PÄÄTTELYLOGIIKAN TARKISTUS:
Jokaisen päättelykysymyksen looginen ketju:
1. PREMISSI 1: Ensimmäinen johtolanka esittää tosiseikan A
2. PREMISSI 2: Toinen johtolanka esittää tosiseikan B
3. PÄÄTELMÄ: Tosiseikat A ja B yhdessä johtavat loogisesti johtopäätökseen C
4. VASTAUS: Johtopäätös C vastaa päättelykysymykseen

HARHAUTUSTEN SUUNNITTELU:
Ylimääräiset johtolangat (harhautukset):
- Liittyvät tapaukseen ja vaikuttavat relevan­teilta
- Eivät muodosta loogista paria minkään muun johtolangan kanssa
- Voivat olla osittain tosia mutta epätäydellisiä
- Eivät saa olla ristiriidassa oikeiden johtolankojen kanssa

TODISTEIDEN TYYPIT:
Käytä monipuolisia todistetyyppejä: todistajanlausunnot, fyysiset esineet, aikaleimoja, sijaintitietoja, motiiveja, asiakirjatietoja. Yhdistä eri tyyppejä samassa kysymyksessä.

ESIMERKKI:
"Kuka kavalsi varoja?" → Johtolanka 1: "velkoja" (motiivi), Johtolanka 2: "pankkiautomaatti" (mahdollisuus) → Looginen yhteys: MOTIIVI + MAHDOLLISUUS

JOHTOLANKASANOJEN VAATIMUKSET:

KRIITTISEN TÄRKEÄÄ - KAKSI ERI KENTTÄÄ:
Jokainen johtolanka sisältää KAKSI erillistä kenttää, joilla on ERI tarkoitukset:
1. "keyword.word" = TÄSMÄLLINEN KOPIO tekstistä (PAKOLLINEN että löytyy tekstistä)
2. "name" = Johtolangan näyttönimi (voi olla perusmuoto tai muu muoto, käytetään vain näyttämiseen)

VAROITUS: ÄLÄ SEKOITA NÄITÄ KENTTIÄ!
- keyword.word TÄYTYY olla TÄSMÄLLINEN KOPIO tekstistä
- name voi olla eri muoto (esim. perusmuoto)

JOHTOLANKASANOJEN VALINTA JA LUOMINEN:
1. Valitse johtolankasanat asiakirjan pääkäsitteistä ja kokonaisuuden kannalta tärkeistä asioista
2. Johtolankasanojen TÄYTYY löytyä asiakirjojen "content"-kentistä

PAKOLLINEN PROSESSI JOKAISELLE JOHTOLANKASANALLE:

VAIHE 1 - ETSI TEKSTISTÄ:
- Etsi tekstistä sopiva sana tai sanapari joka on tärkeä tapauksen kannalta
- Käytä tekstihaku-toimintoa löytääksesi sanan sijainnin tekstissä
- Varmista että sana todella esiintyy tekstissä

VAIHE 2 - KOPIOI TÄSMÄLLEEN:
- KOPIOI sana SUORAAN tekstistä (älä kirjoita muistista)
- SÄILYTÄ täsmälleen sama:
  * Taivutusmuoto (esim. "korvakorussa", EI "korvakoru")
  * Kirjoitusasu (isot/pienet kirjaimet eivät ole kriittisiä, mutta muoto on)
  * Sanajärjestys (jos monisanainen: "maalattu puulle", EI "puulle maalattu")
  * Välilyönnit (jos monisanainen)

VAIHE 3 - ASETA OIKEIN JSON-RAKENTEESEEN:
- KOPIOIMASI täsmällinen muoto → keyword.word kenttään
- Näyttönimi (voi olla perusmuoto) → name kenttään

ESIMERKKEJÄ:

✅ OIKEIN:
Teksti: "Uhri kantoi korvakorussa pientä timanttia"
→ keyword.word: "korvakorussa" (täsmällinen kopio)
→ name: "korvakoru" (perusmuoto näyttöä varten)

Teksti: "Maalaus oli tehty öljyväreillä kankaalle"
→ keyword.word: "öljyväreillä" (täsmällinen kopio)
→ name: "öljyväri" (perusmuoto näyttöä varten)

Teksti: "Todistaja näki sinisen auton pakenemassa"
→ keyword.word: "sinisen auton" (täsmällinen kopio, monisanainen)
→ name: "sininen auto" (perusmuoto näyttöä varten)

❌ VÄÄRIN:
Teksti: "Uhri kantoi korvakorussa pientä timanttia"
→ keyword.word: "korvakoru" (VÄÄRIN - perusmuoto ei ole tekstissä!)
→ name: "korvakoru"

Teksti: "Todistaja näki sinisen auton pakenemassa"
→ keyword.word: "sininen auto" (VÄÄRIN - perusmuoto ei ole tekstissä!)
→ name: "sininen auto"

Teksti: "Todistaja näki sinisen auton pakenemassa"
→ keyword.word: "auton sinisen" (VÄÄRIN - väärä sanajärjestys!)
→ name: "sininen auto"

VALIDOINTI (PAKOLLINEN):
Ennen johtolangan lisäämistä clues-taulukkoon:
1. Etsi keyword.word tekstistä tekstihakutoiminnolla
2. Jos EI löydy → ÄLÄ lisää johtolankaa, valitse toinen sana
3. Jos löytyy → varmista että se on TÄSMÄLLEEN sama merkkijono (taivutus, järjestys, välilyönnit)
4. Tallenna myös caseFileIndex: miltä tekstiltä (0, 1, 2, jne.) sana löytyy

MUUT VAATIMUKSET:
- Jokainen keyword.word saa esiintyä vain KERRAN koko pelissä
- Johtolankasanat eivät saa olla tyhjiä tai sisältää vain välilyöntejä
- Kahden johtolankasanan täytyy muodostaa looginen yhteys päättelykysymykseen (MOTIIVI + MAHDOLLISUUS, TODISTUS + VAHVISTUS, jne.)

HENKILÖIDEN MAINITSEMINEN TEKSTEISSÄ:
- Kun henkilö mainitaan ENSIMMÄISTÄ KERTAA asiakirjassa, käytä TÄYTTÄ NIMEÄ (etu- ja sukunimi)
- Anna konteksti: kerro kuka henkilö on ja mikä hänen roolinsa on tapauksessa
  * Esimerkki: "Todistaja Matti Virtanen, 45-vuotias naapuri, kertoi nähneensä..."
  * Esimerkki: "Epäilty Liisa Korhonen, uhrin entinen työkaveri, väitti olleensa..."
- Myöhemmissä maininnoissa voit käyttää pelkkää sukunimeä, kun henkilö on jo esitelty
- Jos henkilön nimi on johtolankasana, varmista että tekstissä mainitaan:
  1. Henkilön koko nimi (etu- ja sukunimi)
  2. Henkilön rooli/asema (todistaja, uhri, epäilty, asiantuntija, jne.)
  3. Henkilön yhteys tapaukseen (miksi hän on mukana tässä asiassa)

TEKSTIEN MUOTOILU:
- TÄRKEÄÄ: tekstien otsikot (title) täytyy pitää lyhyinä ja ytimekkäinä, maksimissaan 3 sanaa
- KRIITTISEN TÄRKEÄÄ: Kirjoita asiakirjat TÄYSINÄ KAPPALEINA kokonaisina virkkeinä, ÄLÄ listapisteinä tai ranskalaisina viivoina
- Tavoitepituus: ${wordCountSpec} - käytä laajoja, yksityiskohtaisia kappaleita
- Tutki asiakirjan otsikko ja kirjoita sisältö sopivassa muodossa
- "Poliisiraportti", "Tutkintakertomus" → virallisen poliisidokumentin tyyli
- "Todistajanlausunto", "Kuulustelu" → haastattelumerkinnät
- "Rikospaikkatutkinta" → tekninen raportti
- "Lääkärinlausunto" → lääketieteellinen dokumentti
- Käytä asianmukaista terminologiaa ja rakennetta kullekin asiakirjatyypille
- Jaa pidemmät asiakirjat selkeisiin kappaleisiin käyttämällä rivinvaihtoja (\n\n)
- Käytä asiakirjatyypille sopivia alaotsikoita (esim. "TAPAHTUMAPAIKKA:", "TODISTEET:", "HAVAINNOT:")
- Lisää tyhjä rivi alaotsikoiden jälkeen paremman luettavuuden vuoksi
- Käytä rivinvaihtoja erottamaan eri asiakokonaisuudet selkeästi toisistaan

VASTAUKSEN MUOTO:
Anna YKSI täydellinen JSON-objekti kaikilla kentillä (title, caseFiles, clues, deductionQuestions, successMessage, failureMessage, requiredClueCount). Älä jaa osiin.

JSON-rakenne:
{
  "title": "Pelin otsikko (2-3 sanaa)",
  "mystery": "Lyhyt kuvaus mysteeristä",
  "introText": "Johdantoteksti joka esittelee tapauksen - EI pelimekaniikka-ohjeita",
  "caseFiles": [{"title": "Asiakirjan otsikko", "content": "Asiakirjan sisältö"}],
  "clues": [{"id": "clue_1", "name": "Johtolangan nimi", "description": "Kuvaus", "keyword": {"word": "avainsana", "caseFileIndex": 0}}],
  "deductionQuestions": [{"id": "question_1", "question": "Päättelykysymys", "correctKeywords": ["avainsana1", "avainsana2"]}],
  "successMessage": "Onnistumisviesti",
  "failureMessage": "Epäonnistumisviesti",
  "requiredClueCount": ${correctKeywordCount}
}

GENEROINTIJÄRJESTYS (PAKOLLINEN - NOUDATA TÄTÄ JÄRJESTYSTÄ):
1. Luo ENSIN kaikki ${caseFileCount} tekstiä "caseFiles"-taulukkoon
   - Kirjoita tekstit kokonaan valmiiksi ENNEN kuin siirryt johtolankoihin
   - Varmista että tekstit sisältävät riittävästi tärkeitä sanoja/käsitteitä

2. SITTEN luo ${totalKeywordCount} johtolankaa "clues"-taulukkoon
   - Käy JOKAINEN teksti läpi
   - ETSI ja KOPIOI täsmälliset sanat teksteistä → keyword.word -kenttiin
   - Luo näyttönimet → name -kenttiin
   - VARMISTA että JOKAINEN keyword.word löytyy tekstistä (käytä tekstihakua)
   - Tallenna caseFileIndex: mistä tekstistä sana löytyy

3. SITTEN luo ${questionCount} kysymystä "deductionQuestions"-taulukkoon
   - KRIITTINEN: correctKeywords-arvot TÄYTYY kopioida TÄSMÄLLEEN samat kuin clues[].keyword.word -kentissä
   - ÄLÄ KEKSI uusia keyword-arvoja tässä vaiheessa

4. LOPUKSI validoi ennen JSON:n palauttamista:
   - Jokainen clues[].keyword.word löytyy caseFiles[].content -tekstistä? (Etsi tekstihakutoiminnolla!)
   - Jokainen correctKeywords-arvo löytyy clues[].keyword.word -taulukosta?
   - correctKeywords sisältää tasan 2 eri avainsanaa per kysymys?
   - Jos jokin validointi epäonnistuu → KORJAA virhe, ÄLÄ palauta virheellistä dataa

TÄRKEÄÄ:
- Generoi täsmälleen ${totalKeywordCount} johtolankaa ja ${questionCount} kysymystä
- correctKeywords-arvot TÄYTYY olla TÄSMÄLLEEN SAMAT kuin clues[].keyword.word (sama kirjoitusasu, taivutus, välilyönnit)
- Johtolankasanojen täytyy löytyä caseFiles[].content -teksteistä täsmälleen samassa muodossa

${targetLanguagePrompt}`;
		}

		return { system: systemPrompt, user: userPrompt };
	}

	/**
	 * Process AI response into PluginDetectiveGameData
	 * @param response The AI service response
	 * @param parameters Original generation parameters
	 * @returns Generation result with game data or error
	 */
	processResponse(response: any, parameters: any): GenerationResult {
		try {
			const json = response.json;

			if (!json) {
				return {
					success: false,
					error: 'Invalid response format: no JSON found',
					rawResponse: response.rawResponse,
				};
			}

			// Validate required fields with detailed error messages
			const missingFields: string[] = [];
			if (!json.title) missingFields.push('title');
			if (!json.caseFiles) missingFields.push('caseFiles (field missing)');
			else if (!Array.isArray(json.caseFiles)) missingFields.push('caseFiles (not an array)');
			else if (json.caseFiles.length === 0) missingFields.push('caseFiles (empty array)');

			if (!json.clues) missingFields.push('clues (field missing)');
			else if (!Array.isArray(json.clues)) missingFields.push('clues (not an array)');
			else if (json.clues.length === 0) missingFields.push('clues (empty array)');

			if (!json.deductionQuestions) missingFields.push('deductionQuestions (field missing)');
			else if (!Array.isArray(json.deductionQuestions)) missingFields.push('deductionQuestions (not an array)');
			else if (json.deductionQuestions.length === 0) missingFields.push('deductionQuestions (empty array)');

			if (missingFields.length > 0) {
				console.error('AI Generation validation failed. Missing or invalid fields:', missingFields);
				console.error('Received JSON structure:', JSON.stringify(json, null, 2));
				return {
					success: false,
					error: `Missing or invalid fields: ${missingFields.join(', ')}`,
					rawResponse: response.rawResponse,
				};
			}

			// Create game data
			const gameData = new PluginDetectiveGameData();

			// Set basic properties
			gameData.title = json.title || 'Mysteeritapaus';
			gameData.mystery = json.mystery || 'Ratkaise mysteeri tutkimalla todisteita.';
			gameData.introText = json.introText || 'Tutustu tapaukseen ja selvitä totuus.';
			gameData.successMessage = json.successMessage || 'Erinomaista! Ratkaisit mysteerin.';
			gameData.failureMessage = json.failureMessage || 'Päättelysi ei ole oikein. Yritä uudelleen.';
			const questionCount = parameters['questionCount'] || 3;
			gameData.requiredClueCount = json.requiredClueCount || questionCount * 2;

			// Process case files (now without clues)
			gameData.caseFiles = json.caseFiles.map((caseFile: any) => ({
				title: caseFile.title || 'Asiakirja',
				content: caseFile.content || '',
			} as DetectiveCaseFile));

			// Process individual clues (each contains one keyword)
			gameData.clues = (json.clues || []).map((clue: any) => ({
				id: clue.id || `clue_${Math.random().toString(36).substr(2, 9)}`,
				name: clue.name || 'Johtolanka',
				description: clue.description || '',
				keyword: {
					word: clue.keyword?.word || '',
					caseFileIndex: clue.keyword?.caseFileIndex || 0
				},
				caseFileIndex: clue.keyword?.caseFileIndex || 0,
			} as DetectiveDeductionClue));

			// Auto-correct case file indices for clues
			this.correctClueIndices(gameData);

			// Process deduction questions from the separate deductionQuestions array
			gameData.deductionQuestions = (json.deductionQuestions || []).map((question: any) => {
				// Find the clue IDs that correspond to the correct keywords
				const correctKeywords = question.correctKeywords || [];

				// Use case-insensitive and trimmed comparison to match keywords
				const firstKeywordClue = gameData.clues.find(clue =>
					clue.keyword.word.trim().toLowerCase() === correctKeywords[0]?.trim().toLowerCase()
				);
				const secondKeywordClue = gameData.clues.find(clue =>
					clue.keyword.word.trim().toLowerCase() === correctKeywords[1]?.trim().toLowerCase()
				);

				// Log warnings if keywords don't match, and show available keywords
				if (!firstKeywordClue && correctKeywords[0]) {
					const availableKeywords = gameData.clues.map(c => c.keyword.word).join('", "');
					console.warn(`Could not find clue for keyword: "${correctKeywords[0]}" in question: "${question.question}"`);
					console.warn(`Available keywords in clues: "${availableKeywords}"`);
				}
				if (!secondKeywordClue && correctKeywords[1]) {
					const availableKeywords = gameData.clues.map(c => c.keyword.word).join('", "');
					console.warn(`Could not find clue for keyword: "${correctKeywords[1]}" in question: "${question.question}"`);
					console.warn(`Available keywords in clues: "${availableKeywords}"`);
				}

				return {
					id: question.id || `question_${Math.random().toString(36).substr(2, 9)}`,
					question: question.question || '',
					correctFirstClueId: firstKeywordClue?.id || '',
					correctSecondClueId: secondKeywordClue?.id || '',
				};
			});

			// Validate that clue keywords actually appear in case files
			const invalidClues: string[] = [];
			gameData.clues.forEach(clue => {
				const caseFileIndex = clue.keyword.caseFileIndex;
				if (caseFileIndex >= 0 && caseFileIndex < gameData.caseFiles.length) {
					const caseFileContent = gameData.caseFiles[caseFileIndex].content;
					if (!caseFileContent.toLowerCase().includes(clue.keyword.word.toLowerCase())) {
						invalidClues.push(`Clue "${clue.name}": keyword "${clue.keyword.word}" not found in case file ${caseFileIndex + 1}`);
					}
				}
			});

			// Log warnings for invalid clues but don't fail the generation
			if (invalidClues.length > 0) {
				console.warn('AI Generated invalid clue keywords:', invalidClues);
			}

			// Generate deduction slots from correct clues
			gameData.generateDeductionSlots();

			return {
				success: true,
				gameData,
				rawResponse: response.rawResponse,
			};
		} catch (error) {
			return {
				success: false,
				error: `Response processing failed: ${error}`,
				rawResponse: response.rawResponse,
			};
		}
	}

	/**
	 * Validate detective game specific request
	 */
	validateRequest(request: GenerationRequest): ValidationResult {
		const hasSourceMaterial =
			request.sourceMaterial &&
			request.sourceMaterial.fileHandles.length > 0;
		const hasTopic =
			request.parameters['detectiveTopic'] &&
			request.parameters['detectiveTopic'].trim();

		if (!hasSourceMaterial && !hasTopic) {
			return {
				valid: false,
				error: 'Syötä pelin aihe tai valitse lähdemateriaali',
			};
		}

		const caseFileCount = request.parameters['caseFileCount'] || 4;
		const questionCount = request.parameters['questionCount'] || 3;
		const extraClueCount = request.parameters['extraClueCount'] || 2;

		if (caseFileCount < 1) {
			return {
				valid: false,
				error: 'Vähintään 1 asiakirja vaaditaan',
			};
		}

		if (questionCount < 1) {
			return {
				valid: false,
				error: 'Vähintään 1 päättelykysymys vaaditaan',
			};
		}

		if (extraClueCount < 0) {
			return {
				valid: false,
				error: 'Ylimääräisten johtolankojen määrä ei voi olla negatiivinen',
			};
		}

		return { valid: true };
	}

	/**
	 * Get default parameters for detective game
	 */
	getDefaultParameters(): any {
		return {
			detectiveTopic: '',
			caseFileCount: 4,
			questionCount: 3,
			extraClueCount: 2,
			gameDuration: 1,
			clearPrevious: false,
			selectedInterfaceId: 0,
		};
	}

	/**
	 * Merge new content with existing game data
	 * @param existingGameData Current game data
	 * @param newGameData Generated game data
	 * @param clearPrevious Whether to clear existing content
	 * @returns Updated game data
	 */
	mergeContent(
		existingGameData: PluginDetectiveGameData,
		newGameData: PluginDetectiveGameData,
		clearPrevious: boolean,
	): PluginDetectiveGameData {
		if (clearPrevious) {
			// Replace everything with new data
			return newGameData;
		} else {
			// For detective games, we typically replace the entire mystery
			// as it's a cohesive narrative that doesn't merge well
			return newGameData;
		}
	}

	/**
	 * Auto-correct case file indices for clues that have incorrect caseFileIndex
	 * This fixes AI generation errors where keywords are assigned to wrong case files
	 * @param gameData The game data to correct
	 */
	private correctClueIndices(gameData: PluginDetectiveGameData): void {
		gameData.clues.forEach(clue => {
			const specifiedIndex = clue.keyword.caseFileIndex;
			const keyword = clue.keyword.word;

			// Check if keyword exists in the specified case file
			if (specifiedIndex >= 0 && specifiedIndex < gameData.caseFiles.length) {
				const caseFileContent = gameData.caseFiles[specifiedIndex].content;
				if (caseFileContent.toLowerCase().includes(keyword.toLowerCase())) {
					// Keyword found in correct case file, no correction needed
					return;
				}
			}

			// Keyword not found in specified case file, search all case files
			for (let i = 0; i < gameData.caseFiles.length; i++) {
				const caseFileContent = gameData.caseFiles[i].content;
				if (caseFileContent.toLowerCase().includes(keyword.toLowerCase())) {
					// Found the keyword in a different case file, update indices
					console.warn(
						`Auto-corrected clue "${clue.name}": keyword "${keyword}" moved from case file ${specifiedIndex + 1} to case file ${i + 1}`
					);
					clue.keyword.caseFileIndex = i;
					clue.caseFileIndex = i;
					return;
				}
			}

			// Keyword not found in any case file - log error but don't change index
			console.error(
				`Clue "${clue.name}": keyword "${keyword}" not found in any case file. Keeping original caseFileIndex: ${specifiedIndex}`
			);
		});
	}
}